import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import TopHeader from '../components/TopHeader';
import { accountingAPI, API_BASE } from '../utils/api';
import { 
  Receipt, Plus, Search, Filter, Download, Edit2, Trash2, 
  Calendar, ChevronLeft, X, Upload, FileText, RefreshCw,
  CheckCircle, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Date filter presets
const DATE_PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'This Quarter', value: 'quarter' },
  { label: 'This Year', value: 'year' },
  { label: 'All Time', value: 'all' },
];

// Calculate date range based on preset
const getDateRangeFromPreset = (preset) => {
  const today = new Date();
  let startDate, endDate = today.toISOString().split('T')[0];
  
  switch (preset) {
    case 'today':
      startDate = endDate;
      break;
    case 'week':
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      startDate = weekStart.toISOString().split('T')[0];
      break;
    case 'month':
      startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
      break;
    case 'quarter':
      const qMonth = Math.floor(today.getMonth() / 3) * 3;
      startDate = `${today.getFullYear()}-${String(qMonth + 1).padStart(2, '0')}-01`;
      break;
    case 'year':
      startDate = `${today.getFullYear()}-01-01`;
      break;
    case 'all':
      startDate = '';
      endDate = '';
      break;
    default:
      startDate = '';
      endDate = '';
  }
  
  return { startDate, endDate };
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

// Expense categories with icons
const EXPENSE_CATEGORIES = [
  { id: 'salaries', name: 'Salaries & Wages', icon: 'users' },
  { id: 'rent', name: 'Rent & Lease', icon: 'home' },
  { id: 'utilities', name: 'Utilities', icon: 'zap' },
  { id: 'internet', name: 'Internet & Phone', icon: 'wifi' },
  { id: 'subscriptions', name: 'Software & Subscriptions', icon: 'package' },
  { id: 'office_supplies', name: 'Office Supplies', icon: 'clipboard' },
  { id: 'travel', name: 'Travel & Transport', icon: 'car' },
  { id: 'marketing', name: 'Marketing & Advertising', icon: 'megaphone' },
  { id: 'repairs', name: 'Repairs & Maintenance', icon: 'tool' },
  { id: 'legal', name: 'Legal & Professional', icon: 'briefcase' },
  { id: 'insurance', name: 'Insurance', icon: 'shield' },
  { id: 'miscellaneous', name: 'Miscellaneous', icon: 'more-horizontal' },
];

// Type Badge
const TypeBadge = ({ type }) => {
  const isFixed = type === 'fixed';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      isFixed ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
    }`}>
      {isFixed ? 'Fixed' : 'Variable'}
    </span>
  );
};

// Empty form state
const EMPTY_EXPENSE = {
  title: '',
  category: '',
  expense_type: 'variable',
  amount: '',
  date: new Date().toISOString().split('T')[0],
  due_date: '',
  description: '',
  is_recurring: false,
  recurrence_frequency: 'monthly',
  recurrence_start_date: '',
  recurrence_end_date: '',
};

// Calculate days overdue
const getDaysOverdue = (dueDate) => {
  if (!dueDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - due) / (1000 * 60 * 60 * 24));
  return diff;
};

export default function ExpenseManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || '';
  const initialStatus = searchParams.get('status') || '';
  
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(initialType);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [paidFilter, setPaidFilter] = useState(initialStatus === 'overdue' ? 'overdue' : '');
  const [datePreset, setDatePreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState(EMPTY_EXPENSE);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Summary stats
  const [stats, setStats] = useState({
    total: 0,
    fixed: 0,
    variable: 0,
    thisMonth: 0,
  });

  // Handle date preset change
  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    const { startDate, endDate } = getDateRangeFromPreset(preset);
    setDateFrom(startDate);
    setDateTo(endDate);
  };

  // Fetch expenses data
  useEffect(() => {
    fetchExpenses();
  }, [dateFrom, dateTo, typeFilter, categoryFilter]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.start_date = dateFrom;
      if (dateTo) params.end_date = dateTo;
      if (typeFilter) params.expense_type = typeFilter;
      if (categoryFilter) params.category = categoryFilter;
      
      const response = await accountingAPI.getExpenses(params);
      setExpenses(response.data);
      
      // Calculate stats
      const data = response.data;
      const today = new Date();
      const monthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
      
      setStats({
        total: data.reduce((sum, e) => sum + e.amount, 0),
        fixed: data.filter(e => e.expense_type === 'fixed').reduce((sum, e) => sum + e.amount, 0),
        variable: data.filter(e => e.expense_type === 'variable').reduce((sum, e) => sum + e.amount, 0),
        thisMonth: data.filter(e => e.date >= monthStart).reduce((sum, e) => sum + e.amount, 0),
        paid: data.filter(e => e.is_paid).reduce((sum, e) => sum + e.amount, 0),
        due: data.filter(e => !e.is_paid).reduce((sum, e) => sum + e.amount, 0),
        paidCount: data.filter(e => e.is_paid).length,
        dueCount: data.filter(e => !e.is_paid).length,
      });
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  // Filter expenses by search
  useEffect(() => {
    if (!search) {
      setFilteredExpenses(expenses);
    } else {
      const term = search.toLowerCase();
      setFilteredExpenses(expenses.filter(e => 
        e.title?.toLowerCase().includes(term) ||
        e.category?.toLowerCase().includes(term) ||
        e.description?.toLowerCase().includes(term)
      ));
    }
  }, [search, expenses]);

  // Handle form change
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Open modal for new expense
  const handleAddNew = () => {
    setEditingExpense(null);
    setFormData(EMPTY_EXPENSE);
    setAttachmentFile(null);
    setShowModal(true);
  };

  // Open modal for editing
  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      title: expense.title || '',
      category: expense.category || '',
      expense_type: expense.expense_type || 'variable',
      amount: expense.amount || '',
      date: expense.date || '',
      description: expense.description || '',
      is_recurring: expense.is_recurring || false,
      recurrence_frequency: expense.recurrence_frequency || 'monthly',
      recurrence_start_date: expense.recurrence_start_date || '',
      recurrence_end_date: expense.recurrence_end_date || '',
    });
    setAttachmentFile(null);
    setShowModal(true);
  };

  // Save expense
  const handleSave = async () => {
    if (!formData.title || !formData.category || !formData.amount || !formData.date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', formData.title);
      fd.append('category', formData.category);
      fd.append('expense_type', formData.expense_type);
      fd.append('amount', parseFloat(formData.amount) || 0);
      fd.append('date', formData.date);
      fd.append('description', formData.description || '');
      fd.append('is_recurring', formData.is_recurring);
      
      if (formData.is_recurring) {
        fd.append('recurrence_frequency', formData.recurrence_frequency);
        if (formData.recurrence_start_date) fd.append('recurrence_start_date', formData.recurrence_start_date);
        if (formData.recurrence_end_date) fd.append('recurrence_end_date', formData.recurrence_end_date);
      }
      
      if (attachmentFile) {
        fd.append('file', attachmentFile);
      }
      
      if (editingExpense) {
        await accountingAPI.updateExpense(editingExpense.id, fd);
        toast.success('Expense updated successfully');
      } else {
        await accountingAPI.createExpense(fd);
        toast.success('Expense created successfully');
      }
      
      setShowModal(false);
      fetchExpenses();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  // Delete expense
  const handleDelete = async (expense) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      await accountingAPI.deleteExpense(expense.id);
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  // Mark expense as paid
  const handleMarkPaid = async (expense) => {
    try {
      await accountingAPI.markExpensePaid(expense.id, {
        paid_date: new Date().toISOString().split('T')[0]
      });
      toast.success('Expense marked as paid');
      fetchExpenses();
    } catch (error) {
      console.error('Error marking expense as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to update expense');
    }
  };

  // Mark expense as unpaid
  const handleMarkUnpaid = async (expense) => {
    try {
      await accountingAPI.markExpenseUnpaid(expense.id);
      toast.success('Expense marked as unpaid');
      fetchExpenses();
    } catch (error) {
      console.error('Error marking expense as unpaid:', error);
      toast.error(error.response?.data?.detail || 'Failed to update expense');
    }
  };

  // Export to CSV
  const handleExport = () => {
    const headers = ['Title', 'Category', 'Type', 'Amount', 'Date', 'Description', 'Recurring'];
    const rows = filteredExpenses.map(e => [
      e.title,
      e.category,
      e.expense_type,
      e.amount,
      e.date,
      e.description || '',
      e.is_recurring ? 'Yes' : 'No'
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Get category name
  const getCategoryName = (id) => {
    const cat = EXPENSE_CATEGORIES.find(c => c.id === id);
    return cat?.name || id?.replace('_', ' ').toUpperCase();
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="expense-management">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-[260px]">
        <TopHeader />
        <main className="flex-1 p-6 overflow-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/accounting')}>
              <ChevronLeft size={18} />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-800">Expense Management</h1>
              <p className="text-sm text-slate-500">Track and manage all business expenses</p>
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={handleExport}>
              <Download size={16} />
              Export
            </Button>
            <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={handleAddNew}>
              <Plus size={16} />
              Add Expense
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-6 gap-4 mb-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Total Expenses</div>
                <div className="text-xl font-bold text-slate-800">{formatCurrency(stats.total)}</div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Fixed</div>
                <div className="text-xl font-bold text-blue-600">{formatCurrency(stats.fixed)}</div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Variable</div>
                <div className="text-xl font-bold text-purple-600">{formatCurrency(stats.variable)}</div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Paid</div>
                <div className="text-xl font-bold text-emerald-600">{formatCurrency(stats.paid)}</div>
                <div className="text-xs text-slate-400">{stats.paidCount || 0} expenses</div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Due</div>
                <div className="text-xl font-bold text-amber-600">{formatCurrency(stats.due)}</div>
                <div className="text-xs text-slate-400">{stats.dueCount || 0} expenses</div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">This Month</div>
                <div className="text-xl font-bold text-slate-600">{formatCurrency(stats.thisMonth)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="border-0 shadow-sm mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      placeholder="Search expenses..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-36" data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="variable">Variable</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={categoryFilter || 'all'} onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-48" data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {EXPENSE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={datePreset} onValueChange={handleDatePresetChange}>
                  <SelectTrigger className="w-36" data-testid="date-preset-filter">
                    <Calendar size={14} className="mr-2 text-slate-500" />
                    <SelectValue placeholder="Date Range" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_PRESETS.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value}>{preset.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setDatePreset('custom'); }}
                    className="w-36"
                  />
                  <span className="text-slate-400">to</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setDatePreset('custom'); }}
                    className="w-36"
                  />
                </div>
                {(typeFilter || categoryFilter || dateFrom || dateTo) && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setTypeFilter('');
                      setCategoryFilter('');
                      setDateFrom('');
                      setDateTo('');
                      setDatePreset('all');
                    }}
                  >
                    <X size={16} className="mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Expenses Table */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                </div>
              ) : filteredExpenses.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Receipt size={48} className="mx-auto mb-3 text-slate-300" />
                  <p>No expenses found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Title</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Category</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Type</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Due Date</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Status</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Recurring</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredExpenses.map((expense) => {
                        const daysOverdue = getDaysOverdue(expense.due_date);
                        const isOverdue = !expense.is_paid && daysOverdue !== null && daysOverdue > 0;
                        
                        return (
                        <tr key={expense.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${isOverdue ? 'bg-red-50/30' : ''}`}>
                          <td className="py-3 px-4">
                            <div className="font-medium text-slate-800">
                              {expense.is_carry_forward && <span className="text-amber-600">[CF] </span>}
                              {expense.title?.replace('[CF] ', '')}
                            </div>
                            {expense.description && (
                              <span className="text-xs text-slate-500 line-clamp-1">{expense.description}</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">{getCategoryName(expense.category)}</td>
                          <td className="py-3 px-4 text-center">
                            <TypeBadge type={expense.expense_type} />
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-slate-800">{formatCurrency(expense.amount)}</td>
                          <td className="py-3 px-4 text-sm text-slate-600">{expense.date}</td>
                          <td className="py-3 px-4 text-sm">
                            {expense.due_date ? (
                              <div>
                                <span className={isOverdue ? 'text-red-600 font-medium' : 'text-slate-600'}>{expense.due_date}</span>
                                {isOverdue && (
                                  <div className="text-xs text-red-500 font-medium">{daysOverdue} days overdue</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {expense.is_paid ? (
                              <button 
                                onClick={() => handleMarkUnpaid(expense)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                                title="Click to mark as unpaid"
                              >
                                <CheckCircle size={12} />
                                Paid
                              </button>
                            ) : (
                              <button 
                                onClick={() => handleMarkPaid(expense)}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                                  isOverdue 
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                } transition-colors`}
                                title="Click to mark as paid"
                              >
                                <Clock size={12} />
                                {isOverdue ? 'Overdue' : 'Due'}
                              </button>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {expense.is_recurring ? (
                              <span className="inline-flex items-center gap-1 text-xs text-teal-600">
                                <RefreshCw size={12} />
                                {expense.recurrence_frequency}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {expense.attachment_url ? (
                              <a 
                                href={`${API_BASE.replace('/api', '')}${expense.attachment_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-teal-600 hover:underline"
                              >
                                <FileText size={14} />
                                View
                              </a>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => handleEdit(expense)}
                                title="Edit"
                              >
                                <Edit2 size={16} className="text-slate-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => handleDelete(expense)}
                                title="Delete"
                              >
                                <Trash2 size={16} className="text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>

      {/* Add/Edit Expense Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g. Office Rent - January"
                data-testid="expense-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                  <SelectTrigger data-testid="expense-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={formData.expense_type} onValueChange={(v) => handleChange('expense_type', v)}>
                  <SelectTrigger data-testid="expense-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed (Recurring)</SelectItem>
                    <SelectItem value="variable">Variable (One-time)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="0"
                  data-testid="expense-amount"
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  data-testid="expense-date"
                />
              </div>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={formData.due_date || ''}
                onChange={(e) => handleChange('due_date', e.target.value)}
                placeholder="Payment due date"
              />
              <p className="text-xs text-slate-400 mt-1">When should this expense be paid?</p>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
            
            {/* Recurring Options */}
            <div className="flex items-center gap-3 py-2">
              <Switch
                checked={formData.is_recurring}
                onCheckedChange={(v) => handleChange('is_recurring', v)}
                id="recurring"
              />
              <Label htmlFor="recurring" className="cursor-pointer">This is a recurring expense</Label>
            </div>
            
            {formData.is_recurring && (
              <div className="space-y-4 pl-4 border-l-2 border-teal-200">
                <div>
                  <Label>Frequency</Label>
                  <Select value={formData.recurrence_frequency} onValueChange={(v) => handleChange('recurrence_frequency', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={formData.recurrence_start_date}
                      onChange={(e) => handleChange('recurrence_start_date', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>End Date (Optional)</Label>
                    <Input
                      type="date"
                      value={formData.recurrence_end_date}
                      onChange={(e) => handleChange('recurrence_end_date', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Attachment */}
            <div>
              <Label>Attachment (Invoice/Receipt)</Label>
              <div className="mt-1">
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-teal-500 transition-colors">
                  <Upload size={20} className="text-slate-400" />
                  <span className="text-sm text-slate-500">
                    {attachmentFile ? attachmentFile.name : 'Click to upload file'}
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => setAttachmentFile(e.target.files[0])}
                  />
                </label>
              </div>
              {editingExpense?.attachment_url && !attachmentFile && (
                <p className="text-xs text-slate-500 mt-1">
                  Current: {editingExpense.attachment_name || 'Attached file'}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? 'Saving...' : editingExpense ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
